import React, { useState, useEffect } from 'react'
import { ChevronLeft, ChevronRight, Plus, Calendar as CalendarIcon, Filter, Search } from 'lucide-react'
import { templateAPI } from '../../services/api'
import EventCreationForm from '../../components/Calendar/EventCreationForm'
import EventDetailsModal from '../../components/Calendar/EventDetailsModal'

const CalendarPage = () => {
  const [currentDate, setCurrentDate] = useState(new Date())
  const [events, setEvents] = useState([])
  const [calendarData, setCalendarData] = useState({})
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showEventModal, setShowEventModal] = useState(false)
  const [selectedDate, setSelectedDate] = useState(null)
  const [selectedEvent, setSelectedEvent] = useState(null)
  const [showEventDetailsModal, setShowEventDetailsModal] = useState(false)
  const [filters, setFilters] = useState({
    event_type: '',
    priority: '',
    status: ''
  })

  useEffect(() => {
    fetchEvents()
    fetchCalendarData()
    fetchTemplates()
  }, [currentDate, filters])

  const fetchEvents = async () => {
    try {
      setLoading(true)
      
      // Calculate date range (45 days back, 45 days forward)
      const startDate = new Date(currentDate)
      startDate.setDate(startDate.getDate() - 45)
      
      const endDate = new Date(currentDate)
      endDate.setDate(endDate.getDate() + 45)
      
      const params = {
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
        ...filters
      }

      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '') {
          delete params[key]
        }
      })
      
      const response = await templateAPI.getCalendarEvents(params)
      setEvents(response.data || [])
    } catch (err) {
      console.error('Error fetching events:', err)
      setError('Failed to load events')
    } finally {
      setLoading(false)
    }
  }

  const fetchCalendarData = async () => {
    try {
      // Calculate date range (45 days back, 45 days forward)
      const startDate = new Date(currentDate)
      startDate.setDate(startDate.getDate() - 45)
      
      const endDate = new Date(currentDate)
      endDate.setDate(endDate.getDate() + 45)
      
      const response = await templateAPI.getCalendarData({
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString()
      })
      
      if (response.data.success) {
        setCalendarData(response.data.data)
      }
    } catch (err) {
      console.error('Error fetching calendar data:', err)
    }
  }

  const fetchTemplates = async () => {
    try {
      const response = await templateAPI.getAll({ limit: 100 })
      if (response.data.success) {
        setTemplates(response.data.data.templates || [])
      }
    } catch (err) {
      console.error('Error fetching templates:', err)
    }
  }

  const handleEventCreated = (newEvent) => {
    fetchEvents()
    setShowEventModal(false)
    setSelectedDate(null)
  }

  const handleEventUpdated = (updatedEvent) => {
    fetchEvents()
    fetchCalendarData()
    setShowEventDetailsModal(false)
    setSelectedEvent(null)
  }

  const handleEventDeleted = (eventId) => {
    fetchEvents()
    fetchCalendarData()
    setShowEventDetailsModal(false)
    setSelectedEvent(null)
  }

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate)
    newDate.setMonth(newDate.getMonth() + direction)
    setCurrentDate(newDate)
  }

  const getDaysInMonth = (date) => {
    const year = date.getFullYear()
    const month = date.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const daysInMonth = lastDay.getDate()
    const startingDayOfWeek = firstDay.getDay()

    const days = []
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null)
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day))
    }
    
    return days
  }

  const formatDateKey = (date) => {
    return date.toISOString().split('T')[0]
  }

  const getEventsForDate = (date) => {
    if (!date) return []
    
    const dateStr = formatDateKey(date)
    return events.filter(event => {
      const eventDate = new Date(event.event_date).toISOString().split('T')[0]
      return eventDate === dateStr
    })
  }

  const getDataForDate = (date) => {
    const dateKey = formatDateKey(date)
    const dayData = calendarData[dateKey]
    if (!dayData) {
      return {
        designs: {
          total: 0,
          visible: 0,
          pending: 0,
          in_progress: 0,
          completed: 0,
          public: 0
        }
      }
    }
    return {
      designs: dayData.designs || {
        total: 0,
        visible: 0,
        pending: 0,
        in_progress: 0,
        completed: 0,
        public: 0
      }
    }
  }

  const getEventTypeIcon = (eventType) => {
    const icons = {
      'design_deadline': '🎨',
      'review': '👀',
      'meeting': '🤝',
      'reminder': '⏰',
      'client_feedback': '💬',
      'approval': '✅',
      'delivery': '📦'
    }
    return icons[eventType] || '📅'
  }

  const getPriorityColor = (priority) => {
    const colors = {
      'low': 'bg-green-100 text-green-800',
      'medium': 'bg-yellow-100 text-yellow-800',
      'high': 'bg-orange-100 text-orange-800',
      'urgent': 'bg-red-100 text-red-800'
    }
    return colors[priority] || 'bg-gray-100 text-gray-800'
  }

  const handleDateClick = (date) => {
    setSelectedDate(date)
    setShowEventModal(true)
  }

  const handleEventClick = (event) => {
    setSelectedEvent(event)
    setShowEventDetailsModal(true)
  }

  const renderCalendarDay = (date, index) => {
    if (!date) {
      return <div key={index} className="h-40 border border-gray-200"></div>
    }

    const dayEvents = getEventsForDate(date)
    const dayData = getDataForDate(date)
    const { designs } = dayData
    const isToday = date.toDateString() === new Date().toDateString()
    const isCurrentMonth = date.getMonth() === currentDate.getMonth()

    return (
      <div
        key={index}
        className={`h-40 border border-gray-200 p-2 cursor-pointer hover:bg-gray-50 ${
          !isCurrentMonth ? 'bg-gray-50 text-gray-400' : ''
        } ${isToday ? 'bg-blue-50' : ''}`}
        onClick={() => handleDateClick(date)}
      >
        <div className={`text-sm font-medium mb-1 ${isToday ? 'text-blue-600' : ''}`}>
          {date.getDate()}
        </div>
        <div className="space-y-1 flex-1 flex flex-col">
          {/* Events as badges */}
          <div className="space-y-1">
            {dayEvents.slice(0, 2).map((event, eventIndex) => (
              <div
                key={eventIndex}
                className={`text-xs p-1 rounded truncate cursor-pointer ${getPriorityColor(event.priority)}`}
                onClick={(e) => {
                  e.stopPropagation()
                  handleEventClick(event)
                }}
                style={{ backgroundColor: `${event.color || '#3B82F6'}20`, borderLeft: `3px solid ${event.color || '#3B82F6'}` }}
              >
                <span className="mr-1">{getEventTypeIcon(event.event_type)}</span>
                {event.title}
              </div>
            ))}
            {dayEvents.length > 2 && (
              <div className="text-xs text-gray-500">
                +{dayEvents.length - 2} more
              </div>
            )}
          </div>
          
          {/* Design counts */}
          {designs.total > 0 && (
            <div className="mt-auto pt-1 border-t border-gray-200">
              <div className="text-xs text-gray-600 mb-0.5">
                <span className="font-medium">{designs.total}</span> total
                {designs.visible > 0 && (
                  <span className="ml-1">
                    (<span className="font-medium">{designs.visible}</span> visible)
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-0.5">
                {designs.pending > 0 && (
                  <div className="text-xs text-yellow-600 bg-yellow-100 px-1 rounded">
                    {designs.pending} Pending
                  </div>
                )}
                {designs.in_progress > 0 && (
                  <div className="text-xs text-blue-600 bg-blue-100 px-1 rounded">
                    {designs.in_progress} In Progress
                  </div>
                )}
                {designs.completed > 0 && (
                  <div className="text-xs text-green-600 bg-green-100 px-1 rounded">
                    {designs.completed} Completed
                  </div>
                )}
                {designs.public > 0 && (
                  <div className="text-xs text-purple-600 bg-purple-100 px-1 rounded">
                    {designs.public} Public
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ]

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Calendar</h1>
        <p className="text-gray-600">Manage your events and deadlines</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
        <div className="flex items-center space-x-4">
          <Filter className="w-5 h-5 text-gray-500" />
          <div className="flex items-center space-x-4">
            <select
              value={filters.event_type}
              onChange={(e) => setFilters(prev => ({ ...prev, event_type: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Event Types</option>
              <option value="design_deadline">Design Deadline</option>
              <option value="review">Review</option>
              <option value="meeting">Meeting</option>
              <option value="reminder">Reminder</option>
              <option value="client_feedback">Client Feedback</option>
              <option value="approval">Approval</option>
              <option value="delivery">Delivery</option>
            </select>

            <select
              value={filters.priority}
              onChange={(e) => setFilters(prev => ({ ...prev, priority: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Priorities</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>

            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>
      </div>

      {/* Calendar */}
      <div className="bg-white rounded-lg shadow-sm border">
        {/* Calendar Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-4">
            <h2 className="text-xl font-semibold text-gray-900">
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h2>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => navigateMonth(-1)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-gray-600" />
              </button>
              <button
                onClick={() => setCurrentDate(new Date())}
                className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors"
              >
                Today
              </button>
              <button
                onClick={() => navigateMonth(1)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronRight className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>
          <button
            onClick={() => setShowEventModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Create Event</span>
          </button>
        </div>

        {/* Calendar Grid */}
        <div className="p-6">
          {/* Day Headers */}
          <div className="grid grid-cols-7 gap-0 mb-4">
            {dayNames.map(day => (
              <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Days */}
          <div className="grid grid-cols-7 gap-0">
            {getDaysInMonth(currentDate).map((date, index) => 
              renderCalendarDay(date, index)
            )}
          </div>
        </div>

        {/* Legend */}
        <div className="px-6 py-4 border-t bg-gray-50">
          <div className="flex items-center space-x-6 text-sm">
            <span className="font-medium text-gray-700">Legend:</span>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-100 border border-green-300 rounded"></div>
              <span className="text-green-600">Low</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-yellow-100 border border-yellow-300 rounded"></div>
              <span className="text-yellow-600">Medium</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-orange-100 border border-orange-300 rounded"></div>
              <span className="text-orange-600">High</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-red-100 border border-red-300 rounded"></div>
              <span className="text-red-600">Urgent</span>
            </div>
          </div>
        </div>
      </div>

      {/* Event Creation Modal */}
      <EventCreationForm
        isOpen={showEventModal}
        onClose={() => {
          setShowEventModal(false)
          setSelectedDate(null)
        }}
        selectedDate={selectedDate}
        onEventCreated={handleEventCreated}
        templates={templates}
      />

      {/* Event Details Modal */}
      <EventDetailsModal
        isOpen={showEventDetailsModal}
        onClose={() => {
          setShowEventDetailsModal(false)
          setSelectedEvent(null)
        }}
        event={selectedEvent}
        onEventUpdated={handleEventUpdated}
        onEventDeleted={handleEventDeleted}
        templates={templates}
      />
    </div>
  )
}

export default CalendarPage
